import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useOttOverlay, useOverlay, usePageViewInDay } from '../../../contexts'
import { ViewLayoutWrapper } from './ViewLayout.style'
import { ProgramProvider } from '../ProgramItem/ProgramContext'
import { FooterStrip, Interstitial, OttTaboolaFeed } from '../../AddsComponents'
import { PlayerView } from './PlayerView'
import {
  dfpUtils,
  playerConfig,
  utils,
} from '../../../components/Player/Helpers'
import { ILevels } from '../../../components/Player/Models/PlayerModels'
import { config } from '../../../lib'
import { IConfigFile, sendOTTGA4 } from '../../../utils'
import { ottUtils } from '../ottUtils'
import { useRootStore } from '../../../store'
import dynamic from 'next/dynamic'
import SearchView from './SearchView'
import { RailsView } from './RailsView'
import { useOttPlayer } from '../../contexts/providers/OttPlayerProvider'
import { PocTaboolaSuffixId } from '../../../components/Taboola'
import { useTaboola } from '../../../components/Taboola/useTaboola'
import { asset_type_enum } from '../../types'

const AllPrograms = dynamic(() => import('./AllPrograms'), { ssr: true })
const NotFound = dynamic(() => import('./NotFound'), { ssr: true })

const ViewLayout = ({ configFile }: { configFile: IConfigFile }) => {
  const refAsPath = useRef('')
  const refPageTitle = useRef('')
  const { asPath } = useRouter()
  const {
    searchPageID,
    activePage,
    tree,
    allProgramsID,
    hp,
    programData,
    assetMeta,
    page_type,
  } = useOttOverlay()
  const { setFeedInView } = useOverlay()
  const { videoPlaying } = useOttPlayer()
  const [taboolaSuffixId, setTaboolaSuffixId] =
    useState<null | PocTaboolaSuffixId>(null)

  const { setTaboolaPageType } = useTaboola()

  useEffect(() => {
    ottUtils.autoRefresh({ videoPlaying })
  }, [videoPlaying])

  const cleanPath = useMemo(() => {
    return asPath.split('?')[0].split('#')[0]
  }, [asPath])

  const { setPageTitle, pageType } = useRootStore()

  const foundEpisode = useMemo(
    () => parseInt(programData?.episode + '') || 0,
    [programData?.episode]
  )

  const resetSearchData = () => {
    // setSearchResults({ result: { objects: [] } })
  }

  const railsData = useMemo(() => {
    if (activePage === 'player' || activePage === 'profile') {
      return false
    }
    if (tree?.list && activePage) {
      return tree.list.find(item => item.id === activePage) || false
    }

    return false
  }, [activePage, tree])

  const children = useMemo(() => {
    if (page_type === '404') {
      return <NotFound />
    }
    if (programData) {
      const { seriesId, season, seriesName } = programData
      if (seriesId) {
        return (
          <ProgramProvider
            seriesId={seriesId}
            seriesName={seriesName}
            key={'ProgramProvider'}
            seasonNum={season?.position || null}
            foundEpisode={foundEpisode}
          />
        )
      }
    }
    // const screenIdMatch = cleanPath.match(/\/screen\/([^/]+)/)
    // if (seriesIdMatch) {
    // }
    if (activePage === 'player') {
      const MEDIAID = cleanPath
        .split('/')
        .filter(slug => !!slug)
        .pop()

      return (
        <PlayerView
          configFile={configFile}
          MEDIAID={MEDIAID || ''}
          key={MEDIAID}
        />
      )
    }

    if (activePage === 'profile') {
      const ProfilePage = dynamic(() => import('../Profile/ProfilePage'))
      return <ProfilePage />
    }

    if (railsData) {
      setPageTitle(railsData.name)
      const children = railsData?.children || []
      switch (activePage) {
        case allProgramsID:
          return <AllPrograms key={'AllPrograms'} /*railsData={railsData}*/ />
        case searchPageID:
          return <SearchView railsData={railsData} />
        default:
          return <RailsView data={children} />
      }
    }
    return []
  }, [
    page_type,
    programData,
    activePage,
    railsData,
    foundEpisode,
    cleanPath,
    configFile,
    setPageTitle,
    allProgramsID,
    searchPageID,
  ])

  useEffect(() => {
    resetSearchData()
  }, [cleanPath])

  useEffect(() => {
    const handleScroll = () => {
      const feed = document.querySelector(
        '#taboola-below-vod-thumbnails,#taboola-below-portal-thumbnails,#taboola-below-vod-thumbnails-pl1'
      ) as HTMLElement
      if (feed) {
        setFeedInView(utils.isTaboolaElementInView(feed))
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [setFeedInView])
  const { pageViewInDayCount } = usePageViewInDay()

  useEffect(() => {
    if (!pageViewInDayCount) {
      return
    }
    const levels = [] as ILevels
    cleanPath
      .split('/')
      .filter(slug => !!slug)
      .map((level, i) => {
        levels[`level_${i + 1}_eng` as keyof ILevels] = level
        levels[`level_${i + 1}_heb` as keyof ILevels] = level
      })

    dfpUtils.setAdTargeting({
      pageType,
      levels,
      route: cleanPath,
      AD_TARGET_ENV: config.adTargetEnv || 'prod',
      pageViewInDay: pageViewInDayCount,
    })
  }, [cleanPath, pageType, pageViewInDayCount])

  useEffect(() => {
    if (
      asPath !== refAsPath.current &&
      assetMeta?.title !== refPageTitle.current
    ) {
      refAsPath.current = asPath
      refPageTitle.current = assetMeta?.title || ''
      const pageData = ottUtils.getPageData({
        asPath,
        eventType: 'page',
        pageTitle: assetMeta?.title,
        page_type,
        page_id:
          assetMeta?.assetId && assetMeta.assetId > 0
            ? assetMeta.assetId
            : activePage
            ? parseInt(activePage + '')
            : null,
      })
      sendOTTGA4({
        eventName: playerConfig.inWebView
          ? 'App_page_load'
          : 'Reshet_page_load',
        pageData: pageData,
      })
    }
  }, [activePage, asPath, assetMeta, assetMeta?.title, pageType, page_type])

  useEffect(() => {
    if (activePage === 'player' || asPath.includes('series')) {
      setTaboolaSuffixId(PocTaboolaSuffixId.pageFooterWithVideo)
    } else {
      setTaboolaSuffixId(PocTaboolaSuffixId.pageFooter)
    }
  }, [activePage, hp, asPath])

  useEffect(() => {
    if (
      assetMeta?.type !== asset_type_enum.Live &&
      (activePage === 'player' || asPath.includes('series'))
    ) {
      setTaboolaPageType({ video: 'auto' })
    } else {
      setTaboolaPageType({ category: 'auto' })
    }
  }, [activePage, asPath, assetMeta?.type, setTaboolaPageType])

  if (!activePage) {
    return null
  }
  return (
    <ViewLayoutWrapper key={'ViewLayoutWrapper'} isHp={activePage === hp}>
      <Interstitial key={'Ott-Interstitial'} />
      {/*<TopBanners key={'TopBanners'} />*/}
      {children}
      <FooterStrip key={'FooterStrip'} />
      {taboolaSuffixId && (
        <OttTaboolaFeed key={'OttTaboola'} suffixId={taboolaSuffixId} />
      )}
    </ViewLayoutWrapper>
  )
}
export default ViewLayout
